import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom, map } from 'rxjs';
import { API_ROOT } from './api-service';

const SESSION_STORAGE_KEY = 'southbound-cms.session-token';

interface LoginResponse {
  email: string;
  name: string;
  token: string;
}

interface JwtPayload {
  sub?: string;
  email?: string;
  name?: string;
  exp?: number;
}

export interface AuthenticatedUser {
  id: string;
  email: string;
  name: string;
}

interface SessionState {
  token: string;
  user: AuthenticatedUser;
  expiresAtEpochSeconds: number;
}

@Injectable({
  providedIn: 'root',
})
export class AuthSessionService {
  private httpClient = inject(HttpClient);
  private apiRoot = inject(API_ROOT);
  private sessionSubject = new BehaviorSubject<SessionState | null>(null);
  private loadingSubject = new BehaviorSubject(false);

  readonly user$ = this.sessionSubject.pipe(map((session) => session?.user ?? null));
  readonly isAuthenticated$ = this.sessionSubject.pipe(map((session) => session !== null));
  readonly isLoading$ = this.loadingSubject.asObservable();

  constructor() {
    this.restoreSession();
  }

  async requestOtp(email: string): Promise<void> {
    const normalizedEmail = email.trim();
    await firstValueFrom(this.httpClient.post(`${this.apiRoot}api/auth/request-otp`, {
      email: normalizedEmail,
    }));
  }

  async verifyOtp(email: string, code: string): Promise<void> {
    const response = await firstValueFrom(this.httpClient.post<LoginResponse>(`${this.apiRoot}api/auth/verify-otp`, {
      email: email.trim(),
      code: code.trim(),
    }));

    this.setSession(response.token);
  }

  getAccessToken(): string | null {
    const session = this.sessionSubject.value;
    if (!session) {
      return null;
    }

    if (session.expiresAtEpochSeconds <= Math.floor(Date.now() / 1000)) {
      this.logout();
      return null;
    }

    return session.token;
  }

  logout(): void {
    localStorage.removeItem(SESSION_STORAGE_KEY);
    this.sessionSubject.next(null);
  }

  private restoreSession(): void {
    const token = localStorage.getItem(SESSION_STORAGE_KEY);
    if (!token) {
      return;
    }

    this.setSession(token);
  }

  private setSession(token: string): void {
    const session = parseSessionToken(token);
    if (!session) {
      this.logout();
      return;
    }

    localStorage.setItem(SESSION_STORAGE_KEY, token);
    this.sessionSubject.next(session);
  }
}

function parseSessionToken(token: string): SessionState | null {
  try {
    const [, payload] = token.split('.');
    if (!payload) {
      return null;
    }

    const decodedPayload = JSON.parse(decodeBase64Url(payload)) as JwtPayload;
    if (!decodedPayload.sub || !decodedPayload.email || !decodedPayload.name || !decodedPayload.exp) {
      return null;
    }

    if (decodedPayload.exp <= Math.floor(Date.now() / 1000)) {
      return null;
    }

    return {
      token,
      user: {
        id: decodedPayload.sub,
        email: decodedPayload.email,
        name: decodedPayload.name,
      },
      expiresAtEpochSeconds: decodedPayload.exp,
    };
  } catch {
    return null;
  }
}

function decodeBase64Url(value: string): string {
  const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
  const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), '=');
  return atob(padded);
}
